// Product analytics (PostHog) for the stylist console, hand-wired so every
// privacy-relevant option is visible here rather than left to a default.
//
// The stylist console is not the consumer app, but it is arguably more
// sensitive per-screen: a stylist looks at members' wardrobes, their body
// scans and their digital twin all day. So the same three rules apply, and
// for a stronger reason.
//
// 1. SESSION REPLAY OFF. `disable_session_recording: true`. Recording this
//    console means recording a continuous stream of other people's body scans
//    and wardrobes - members who are not even the person being recorded and
//    cannot consent to it. Masking is not sufficient here: the sensitive
//    content is the imagery itself, not form fields.
// 2. AUTOCAPTURE OFF. Element text in the review queue is member garment
//    names and member emails.
// 3. HOST REQUIRED, NOT DEFAULTED. The SDK defaults to US cloud, and that
//    default would silently become a data-residency decision. The decision is
//    EU (`https://eu.i.posthog.com`), supplied through the environment rather
//    than hardcoded so it can be corrected without a release. No host
//    configured means analytics stays off.
//
// Errors are NOT routed here: `@closet-theory/web-diagnostics` already sends
// them to the backend telemetry endpoint, and that path stays independent of
// whether any vendor is reachable.
import posthog from 'posthog-js';

let started = false;
let live = false;

/** Starts analytics if, and only if, both key and host are configured. */
export function startStylistAnalytics(env: Record<string, string | undefined>): void {
  if (started) return;
  started = true;
  // Both absent is the normal state until the runtime-env cutover: the
  // console loads and behaves identically, and nothing is sent anywhere.
  // The PROJECT token (`phc_…`), the publishable one meant to ship in a
  // client - never a personal API key (`phx_…`). The project id is not read
  // here: it addresses the management API, which the browser never calls.
  const key = env.VITE_POSTHOG_PROJECT_TOKEN;
  const host = env.VITE_POSTHOG_HOST;
  if (!key || !host) return;
  try {
    posthog.init(key, {
      api_host: host,
      autocapture: false,
      disable_session_recording: true,
      // The console is behind a login, so there is no anonymous browsing
      // worth attributing; only create a person once we identify one.
      person_profiles: 'identified_only',
      capture_pageview: true,
    });
    live = true;
  } catch {
    // Analytics must never stop the console from loading.
    live = false;
  }
}

/** `distinct_id` is our own user id, never email - the same identity the
 * backend's server-side events use, so a stylist is one person across both. */
export function identifyStylist(userId: string | null): void {
  if (!live) return;
  try {
    if (userId) posthog.identify(userId);
    else posthog.reset();
  } catch {
    // ignored
  }
}

/** Properties must not carry member content: no garment names, no member
 * emails, no free text. Counts, ids and enum-ish values only. */
export function captureStylistEvent(event: string, properties?: Record<string, unknown>): void {
  if (!live) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // ignored
  }
}
